/**
 * The legend.
 *
 * Every definition the Stock overview uses, in one place, with its version, its unit, what it
 * is measured on and which tables it came from. This page exists so the overview itself can
 * stay free of prose: a number on that screen carries a hover popover pointing at the sentence
 * below, and the sentence is written once.
 *
 * A definition the owner has not confirmed is marked. A working default that nobody has
 * blessed is still a decision, and it should be visible as one.
 */
import { Card, EmptyState, ErrorState, Pill, Skeleton } from '@/components';
import { ApiProblem } from '@/lib/api';
import { fullTimestamp } from '@/lib/format';
import { useLegend } from '@/features/stock/queries';
import './legend.css';

export function LegendPage() {
  const legend = useLegend();
  const problem = legend.error instanceof ApiProblem ? legend.error : null;

  if (legend.isLoading) {
    return (
      <div className="legend-page">
        <Skeleton shape="card" lines={5} label="Loading the legend" />
      </div>
    );
  }

  if (problem || !legend.data) {
    return (
      <div className="legend-page">
        <ErrorState
          message={problem ? problem.message : 'The legend did not load.'}
          requestId={problem ? problem.shortRequestId : null}
          onRetry={() => void legend.refetch()}
        />
      </div>
    );
  }

  const { entries, config, window } = legend.data;

  return (
    <div className="legend-page">
      <Card title="Settings in force">
        <dl className="legend-config">
          <div>
            <dt>Inv value factor</dt>
            <dd className="o-num">{config.inv_value_factor.toFixed(2)}</dd>
          </div>
          <div>
            <dt>Critical days</dt>
            <dd className="o-num">{config.critical_days}</dd>
          </div>
          <div>
            <dt>Low days</dt>
            <dd className="o-num">{config.low_days}</dd>
          </div>
          <div>
            <dt>At risk days</dt>
            <dd className="o-num">{config.at_risk_days}</dd>
          </div>
          <div>
            <dt>Window mode</dt>
            <dd>{window.mode}</dd>
          </div>
          <div>
            <dt>Window</dt>
            <dd>
              {`${fullTimestamp(window.start)} to ${fullTimestamp(window.end)}`}
              <span className="o-muted">{` ${window.days} days, ${window.label}`}</span>
            </dd>
          </div>
          <div>
            <dt>Prior window</dt>
            <dd>{`${fullTimestamp(window.prior_start)} to ${fullTimestamp(window.prior_end)}`}</dd>
          </div>
        </dl>
        {config.pending_todd.length > 0 ? (
          <p className="legend-pending">
            <Pill tone="warn">default, pending Todd</Pill>
            <span className="o-mono">{config.pending_todd.join(', ')}</span>
          </p>
        ) : null}
      </Card>

      <Card title="Definitions">
        {entries.length === 0 ? (
          <EmptyState message="The server published no definitions for this screen." />
        ) : (
          <ul className="legend-list" role="list">
            {entries.map((entry) => (
              <li key={entry.id} className="legend-entry">
                <h3 className="legend-entry__label">
                  {entry.label}
                  {entry.pending_owner ? <Pill tone="warn">default, pending Todd</Pill> : null}
                </h3>
                <p className="legend-entry__definition">{entry.definition}</p>
                <dl className="legend-entry__facts">
                  <div>
                    <dt>Id</dt>
                    <dd className="o-mono">{entry.id}</dd>
                  </div>
                  <div>
                    <dt>Version</dt>
                    <dd className="o-num">{entry.version}</dd>
                  </div>
                  <div>
                    <dt>Unit</dt>
                    <dd>{entry.unit}</dd>
                  </div>
                  <div>
                    <dt>Basis</dt>
                    <dd>{entry.basis}</dd>
                  </div>
                  <div>
                    <dt>Sources</dt>
                    <dd className="o-mono">
                      {entry.sources.length > 0 ? entry.sources.join(', ') : 'none stated'}
                    </dd>
                  </div>
                </dl>
                {entry.never_confuse_with ? (
                  <p className="legend-entry__warning">
                    <Pill tone="crit">not the same as</Pill>
                    <span>{entry.never_confuse_with}</span>
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
